import { Router } from "@angular/router";
import { AsyncSubject, BehaviorSubject } from "rxjs";
import { MEError, MEErrorType } from "../../error/me-error";
import { MEErrorService } from "../../error/me-error.service";
import { HttpResponse } from "@angular/common/http";

export interface MEAPIResponse<EntityType> extends MEBasicAPIResponse {
    entity : EntityType
}

export interface MEBasicAPIResponse {
    message : string;
    status : ResponseStatus;
    validationErrorList : string[],
}

export interface ResponseStatus {
    code : ResponseCode,
    message : string
}

export interface MEBasicAPIResponseHandler {
    code: ResponseCode,
    handle(response: MEBasicAPIResponse) : boolean | MEError;
}

export interface MEEntityAPIResponseHandler<EntityType> {
    code: ResponseCode,
    handle(response : MEAPIResponse<EntityType>) : boolean | MEError
}

export interface MEDownloadAPIResponseHandler {
    handle(response: HttpResponse<ArrayBuffer>) : boolean | MEError;
}


export class MEUrlBasicResponseHandler implements MEBasicAPIResponseHandler {
    constructor(public code: ResponseCode, private url: string, private router: Router) {}
    handle(response: MEBasicAPIResponse): boolean {
        this.router.navigate([this.url]);
        return true;
    }
}

export class MEErrorBasicResponseHandler implements MEBasicAPIResponseHandler {
    constructor(public code: ResponseCode, private srvError : MEErrorService) {}
    handle(response: MEBasicAPIResponse): boolean {
        this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
            code: response.status.code,
            message: response.message != '' ? response.message : response.status.message,
            display: MEErrorType.Popup,
            url: ''
        }])
        return false;
    }
}

export class MEErrorResponseHandler implements MEEntityAPIResponseHandler<any> {
    constructor(public code: ResponseCode, private srvError : MEErrorService) {}
    handle(response: MEAPIResponse<any>): boolean {
        this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
            code: response.status.code,
            message: response.message != '' ? response.message : response.status.message,
            display: MEErrorType.Popup,
            url: ''
        }])
        return false;
    }
}


export class MEMessageFailHandler implements MEBasicAPIResponseHandler {
    constructor(public code: ResponseCode, 
                private srvError : MEErrorService,
                private errMsg: string) {}
    handle(response: MEBasicAPIResponse): boolean {
        this.srvError.showDialog(this.srvError.SERVER_TITLE, [this.errMsg])
        return false;
    }
}

export class MEServerMessageFailHandler implements MEBasicAPIResponseHandler {
	constructor(public code: ResponseCode,
		private srvError: MEErrorService,
		private errMsg: string = "") { }
	handle(response: MEBasicAPIResponse): boolean {
		let msgs : string[] = [response.message];
		if (this.errMsg) {
			msgs.push(this.errMsg);
		}
		this.srvError.showDialog(this.srvError.SERVER_TITLE, msgs)
		return false;
	}
}

export class MEDoNothingBasicResponseHandler implements MEBasicAPIResponseHandler {
    constructor(public code: ResponseCode) {}
    
    handle(response: MEBasicAPIResponse): boolean {
        return true;
    }
}

export class MEDoNothingResponseHandler implements MEEntityAPIResponseHandler<any> {
    constructor (public code: ResponseCode) {}

    handle(response: MEAPIResponse<any>): boolean {
        return true;
    }
}

export class MECallbackResponseHandler implements MEEntityAPIResponseHandler<any> {
    constructor (public code: ResponseCode, private handler: Function, private extraParam: any = {}) {}

    handle(response: MEAPIResponse<any>): boolean {
		return this.handler(response, this.extraParam);
    }
}

export class MECustomTitleErrorResponseHandler implements MEEntityAPIResponseHandler<any> {
    constructor(public code: ResponseCode, 
                private srvError : MEErrorService,
				private title: string,
				private message : string | null = null) { }
    handle(response: MEAPIResponse<any>): boolean {
        this.srvError.showDialog(this.title, [{
            code: response.status.code,
            message: this.message ?? (response.message != '' ? response.message : response.status.message),
            display: MEErrorType.Popup,
            url: ''
        }])
        return false;
    }
}

export class BasicAsyncResponseSuccessHandler<T> implements MEEntityAPIResponseHandler<T> {
    // code = ResponseCode.SUCCESS;

    constructor(public subj : AsyncSubject<T>, public code = ResponseCode.SUCCESS) {}

    handle(response: MEAPIResponse<T>): boolean | MEError {
        this.subj.next(response.entity);
        this.subj.complete();

        return true;
    }
}

export class BasicBehaviorResponseSuccessHandler<T> implements MEEntityAPIResponseHandler<T> {
    code = ResponseCode.SUCCESS;

    constructor(private subj : BehaviorSubject<T>) {}

    handle(response: MEAPIResponse<T>): boolean | MEError {
        this.subj.next(response.entity);

        return true;
    }
}

export class AllCodeResponseHandler<T> implements MEEntityAPIResponseHandler<T> {

	constructor(private subj: AsyncSubject<T> | undefined = undefined, public code = ResponseCode.ALL_CODE_HANDLER, private value: any|null = null) { }

	handle(_response: MEAPIResponse<T>): boolean | MEError {
		if (this.subj) {
			this.subj.next(this.value as T);
			this.subj.complete();
		}
		return true;
	}
}

/**
 * This is a special created class that only use in some extreme situation
 * Usually the default MEErrorBasicResponseHandler/MEErrorResponseHandler is sufficient enough for generate this.srvApi.Post case
 * However, for Add Peer Payee, it use an explicit completedMFA Async<boolean> so this is required to mark complete if there is other non specific error
 */
export class OtherCodeResponseHandler<T> implements MEEntityAPIResponseHandler<T> {

	constructor(private subj: AsyncSubject<T> | undefined = undefined, public code = ResponseCode.ALL_CODE_HANDLER, private srvError: MEErrorService, private value: any|null = null) { }

	handle(response: MEAPIResponse<any>): boolean {
		this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
			code: response.status.code,
			message: response.message != '' ? response.message : response.status.message,
			display: MEErrorType.Popup,
			url: ''
		}])
		if (this.subj) {
			this.subj.next(this.value as T);
			this.subj.complete();
		}
		return false;
	}
}

export enum ResponseCode {
	
	
	SUCCESS											= 0, 	//OK
	
	SYSTEM_UNAVAILABLE								= 1001, // System Unavailable
	SYSTEM_INTERNAL_ERROR							= 1002, // System Internal Error
	VALIDATION_ERROR								= 1003, // Validation Error
	SESSION_TIMEOUT									= 1004, // Session Timeout
	VENDOR_INTERFACE_ERROR      					= 1005, // Vendor Interface Error
	METHOD_DEPRECATED								= 1006, // Method is Deprecated
	REQUEST_EXECUTION_FAILURE						= 1007, // Request execution failure
	EXTERNAL_LOGGING_DISABLED						= 1008, // Extarnal logging is disabled
	ACTION_NOT_SUPPORTED							= 1009, // Action Not Supported
	PARTIAL_ACCOUNT_INFORMATION_UNAVAILABLE			= 1010, // Some account info is unavailable
	
	USER_NOT_FOUND									= 1501, // User Not Found
	USER_UNAUTHORIZED								= 1502, // User Unauthorized
	USER_UNAUTHENTICATED							= 1503, // User Unauthenticated
	USER_FORCED_CHANGE_PASSWORD 					= 1504, // User Change Password Required
	USER_ALREADY_LOGGED_IN	    					= 1505, // User Already Logged In
	USER_PASSWORD_EXPIRED       					= 1506, // User Password Expired
	DEVICE_NOT_SUPPORT          					= 1507, // Device Not Supported
	INVALID_LOGIN_ATTEMPT       					= 1508, // Invalid Login Attempt
	SECURITY_QUESTION_REQUIRED 						= 1509, // Security Question Required
	
	TERMS_AND_CONDITIONS_REQUIRED 					= 1510, // Terms And Conditions Required
	MFA_VALIDATION									= 1511, // Security Code Validation Required
	MFA_NOT_EXPIRED									= 1512, // Security Code Not Expired.
	NO_MULTI_FACTOR_TOKEN							= 1513, // Setting Token Is Failed
	VERSION_OUT_OF_DATE								= 1514, // This app version has been retired. Please install the newest app version from the store to continue.
	ID_SCAN_VALIDATION								= 1515, // ID Scan Validation for Login
	MFA_AUTH_APP									= 1516, // Security Required by Authorization App
	MFA_AUTH_APP_SELF								= 1517, // App needs to self authorize
	MFA_EXPIRED 									= 1519, // Generating a New Valid Pass Phrase Since an Existing Pass Already Expired or Reached Maximum Retries.

	USER_DECINDED_TOC 								= 1520, // User Declined Terms and Conditions
	USER_NO_PERMISSION								= 1521, // Permission denied
	OPERATION_NOT_PERMITTED							= 1522, // Operation Not Permitted
	INVALID_SECURITY_QUESTION_ATTEMPT 				= 1523, // Invalid Security Question Attempt
	MAX_SECURITY_QUESTION_ATTEMPT 					= 1524, // Max Security Question Attempts Exceeded
	IN_DEVELOPMENT_PROCESS							= 1525, // This functionality is in Development process.
	MAX_ATTEMPTS_EXCEEDED 							= 1526, // Max Attempts Exceeded
	TARGET_USER_NOT_FOUND							= 1527, // Target User Not Found
	USER_ALREADY_EXISTS								= 1528, // User with the given ID already exists.
	DATABASE_CORRUPTION_ERROR						= 1529, // Database corruption error
	FORBIDDEN										= 1530, // Request is Forbidden
	UNABLE_TO_CHANGE_SETTINGS						= 1531, // Unable to change settings
	SELF_REGISTRATION_APPLICATIONS_REQUIRED			= 1532, // Self Registration Applications Required
	USER_MISCONFIGURED								= 1533, // User is incorrectly configured
	
	MFA_VALIDATION_REQUIRED							= 1601, // Security code validation required
	MFA_REGISTRATION_REQUIRED						= 1602, // External authenticator registration is required
	MFA_VALIDATION_SKIPPED							= 1603, // MFA validation is not required
	MFA_ACCESS_DENIED								= 1604, // Access denied
	MFA_SWITCH_TO_INTERNAL							= 1605, // Switch to internal MFA
	MFA_STILL_IN_PROGRESS							= 1606, // The MFA process is still in progress

	ACCOUNT_NOT_FOUND								= 2001, // Account Not Found
	ACCOUNT_INVALID									= 2002, // Invalid Account
	ACCOUNT_LOCKED									= 2003, // Locked Account
	ACCOUNT_SUSPENDED           					= 2004, // Account Suspended
	ACCOUNT_CLOSED              					= 2005, // Account Closed
	ACCOUNT_SUSPENDED_LOGIN           				= 2006, // Account Suspended. Please select Forgot Password to reset.
	ACCOUNT_CANNOT_BE_HIDDEN           				= 2007, // Some of the Accounts cannot be hidden.
	
	NO_ACCOUNT_FOUND								= 2041, // No Accounts Found For this User
	NO_HISTORY_FOUND								= 2042, // No  History Found For This Account in Requested Date Range
	NO_PERMISSIONS_FOUND							= 2043, // No Permissions Found For This User
	NO_PENDING_TRANSACTIONS_FOUND					= 2044, // No Pending Transactions found
	MFA_VALIDATION_FAILED							= 2045, // Security Code Invalid
	MFA_CODE_EXPIRED								= 2046, // Security Code Expired
	MAX_TRIES_EXCEEDED								= 2047, // Security Code Maximum Tries Exceeded
	NO_ALERTS_FOUND									= 2048, // No Alerts Found
	MFA_CODE_INVALID								= 2049, // Security Code does not match. Please try again.
	MFA_EXCEED_MAX_RETRY_LOGOFF                     = 2050, // Security Code Maximum Tries Exceeded and Logoff
	MFA_EXCEED_MAX_RETRY_SUSPENSE                   = 2051, // Security Code Maximum Tries Exceeded and Suspense
	
	TRANSACTION_NOT_FOUND							= 2501, // Transaction Not Found
	PENDING_TRANSACTION								= 2502, // Pending Transaction
	FAILED_TRANSACTION								= 2503, // Failed Transaction
	PENDING_THRESHOLD_TRANSACTION					= 2504, // Pending Threshold Transaction
	PENDING_APPROVAL_FAILED							= 2505, // Pending Approval Failed
	CROSS_CURRENCY_SCHEDULED						= 2506, // This transaction is outside of cross-currency posting hours.
	NOT_ALLOWED_SAME_CURRENCY						= 2507, // Same currency transfer is not allowed in this function.
	NOT_ALLOWED_ACCOUNT_CURRENCY					= 2508, // The account currency is not allowed in this function.
	CROSS_CURRENCY_SCHEDULED_WITH_PENDING  			= 2509, // This transaction is outside of business hours. Multi-signature account cannot be used in scheduled transactions.

	
	TRANSACTION_MFA_VALIDATION						= 2511, // Transaction Security Code Validation Required.
	REQUEST_REJECT									= 2512, // Request is not successful.
	
	Newpassword_sameAs_Old							= 2521, // New Password is same as Old One
	Password_NotMatch								= 2522, // Current Password Is Invalid
	Language_Changed								= 2525, // Language Changed Successfully
	Invalid_Language								= 2526, // Invalid Language
	Network_Changed									= 2527, // Network Changed Successfully
	Invalid_Network									= 2528, // Invalid Network
	Email_Changed									= 2529, // Email Address Changed Successfully
	MobileId_Changed								= 2530, // Mobile Id changed Successfully
	
	Timezone_changed								= 2531, // Timezone changed Successfully
	Subscribe_changed								= 2532, // Subscription changed successfully
	Invalid_subscription							= 2533, // Subscription is invalid
	AlertDestination_Changed						= 2534, // Alert Destination changed Successfully
	Invalid_Destination								= 2535, // Alert Destination is Invalid
	Invalid_TextMessage								= 2536, // Text Message is Invalid
	TextMessage_Changed								= 2537, // Text Message Changed Successfully
	NO_TICKERS_FOUND								= 2538, // No Tickers Found
	NO_SECURE_MESSAGES_FOUND						= 2539, // No Secure Messages Found
	
	NO_ORDERS_FOUND									= 2540, // No Orders Found
	BRANCHES_NOT_LOADED								= 2541, // Branches could not be loaded
	BRANCH_CODE_INVALID								= 2542, // Branch code is Invalid
	ORDER_NOT_CREATED								= 2543, // Error while creating Order
	ERROR_PARSING_DATE								= 2544, // Error Parsing Date
	ORDER_CREATION_SUCCESSFULL						= 2545, // Order Creation is Successfull
	NO_CURRENCY_FOR_CASH_ORDER 						= 2546, // No Currency Found for Cash Order
	ORDER_SIGNATURE_REQUIRED						= 2547, // The order has been placed in a pending state and will only be available for employee review when approved by another signer on the account
	ORDER_DAILY_LIMIT_EXCEEDED						= 2548, // Maximum number of orders submitted for today.
	ORDER_ATTACHMENTS_FAILED						= 2549, // Failed to save the order attachments.
	ORDER_APPROVED_BUT_NOT_POSTED			        = 2550, // The order was approved but still requires more signatures.
	ORDER_PAYMENT_FAILED			        		= 2551, // The order payment failed.
	
	NO_DEFAULT_HISTORY_FOUND						= 3000, // No History found
	MOBIBRANCH_FORM_NOT_FOUND 						= 3001, // Mobibranch Application Form Not Found
	MOBIBRANCH_INVALID_TIMESTAMP_QUERY_STR 			= 3002, // Invalid TimeStamp Format
	MOBIBRANCH_FORM_BLOB_FORMAT_CORRUPTED 			= 3003, // Form JSON Data Corrupted.
	MOBIBRANCH_USER_NOT_FOUND   					= 3004, // No Account Found For this User
	MOBIBRANCH_INTERNAL_ERROR   					= 3005, // Applicantion Internal Error
	MOBIBRANCH_FILE_NOW_FOUND  	 					= 3006, // Download File Not Found On Server.
	MOBIBRANCH_JSON_MISSING_FIELD_UUID 				= 3007, // Missing uuId field in POST Json
	MOBIBRANCH_JSON_MISSING_FIELD_CONTRACTSCOUNT 	= 3008, // Missing 'contractsCount' field in POST Json
	MOBIBRANCH_JSON_KEY_NAME_MISMATCH 				= 3009, // Jsonkey is not found in Form's Json Object
	
	MOBIBRANCH_CONTRACT_NOT_FOUND 					= 3010, // PDF Contract Template File Not Found On Server
	MOBIBRANCH_CONTRACT_GENERATION_ERROR 			= 3011, // Contracts generation error
	MOBIBRANCH_NO_MATCH_FOUND 						= 3012, // No Match Found.
	MOBIBRANCH_MESSAGE_SENT_SUCCESS 				= 3013, // Message sent Successfully.
	MOBIBRANCH_MESSAGE_SENT_FAILURE 				= 3014, // Message not Sent.
	MOBIBRANCH_NOTE_NOT_FOUND 						= 3015, // Application Notes not found.
	MOBIBRANCH_APPLICATION_NOT_CREATED				= 3016, // Application not created
	MOBIBRANCH_FORM_SUBMITTED 						= 3017, // Application Submitted Successfully
	MOBIBRANCH_FORM_ROUTED 							= 3018, // Application has been Routed
	FORM_ACCOUNTID_UPDATED 							= 3019, // Application Account Id Updated Successfully
	
	USER_NOT_ATTACHED  								= 3020, // No User attached
	
	MOBIBRANCH_CONTRACT_SIGNATURE_TAG_LOCATION_FAILURE = 3023, // Failed to locate signature tag in Contract
	MOBIBRANCH_APPLICATION_STATUS_NOT_ALLOWED 		= 3024, // Not allowed to use this status
	MOBIBRANCH_APPLICATION_LOANNO_NOT_FOUND 		= 3025, // There is no LoanNo for this UUID
	
	BARCODE_INVALID 								= 3030, // Barcode is invalid.
	BARCODE_IMAGE_REQUIRED 							= 3031, // Barcode Image Is Required.
	BARCODE_NOT_READABLE 							= 3032, // Barcode Image Is Not Readable. = Expired evaluation period or invalid license key error
	IDSCAN_INVALID 									= 3033, // IDScan Webservice Error.
	WEBSERVICE_CALL_ERROR 							= 3034, // Webservice call error.
	
	CREDIT_SCORE_UNDEFINED							= 3040, // Credit score consumer is undefined
	GEEZEO_CONNECTION_UNAVAILABLE					= 3041, // Geezeo connection is unavailable
	GEEZEO_INVALID_USERNAME							= 3042, // UserId is Invalid for Geezeo
	GEEZEO_INVALID_APIKEY							= 3043, // Invalid API Key
	MOBIBRANCH_FORM_STATUS_UPDATED 					= 3044, // Application Form Status Updated Successfully
	MOBIBRANCH_FORM_STATUS_NOT_UPDATED 				= 3045, // Mobibranch Application Form Status Not Updated
	NO_PAYMENT_FOUND 								= 3046, // Payment is Empty
	
	SECURITY_QUESTION_IS_EMPTY 						= 3060, // Security question list is empty
	FAILED_SET_SECURITY_QUESTION 					= 3061, // Failed set security question
	ANSWER_FORMAT_IS_INVALID_FORMAT 				= 3062, // Answer must be letters, // numbers, // periods, // commas, // ampersand and dashe
	ANSWER_FORMAT_IS_INVALID_LENGTH 				= 3063, // Answer length must be between 3 to 15 characters
	SECURITY_QUESTION_INVALID 						= 3064, // Security question is invalid
	ANSWERS_CANNOT_MATCH		 					= 3065, // Security question answers are not allowed to match each other
	
	AccountNumber_NoChars 							= 3071, // Account Number cannot contain characters
	AccountNumber_EightDigits 						= 3072, // Account Number must be of length 8
	InvalidChequeQuantity							= 3073, // Number of Cheques can be either 1, //5, //10, //20, //30
	InvalidPickupTime 								= 3074, // Invalid Pickup Time
	PickupDateFuture 								= 3075, // Pickup date should be future date
	Invalid_Account 								= 3076, // Invalid Account
	Message_IS_REQUIRED								= 3077, // Message is required
	MISSING_ARGUMENTS 								= 3078, // One or more mandatory arguments are missing in the request
	INVALID_MFA										= 3079, // MFA Code is invalid
	
	TERMS_AND_CONDITIONS_ACCEPTED					= 3080, // Terms and Conditions accepted
	NO_BILL_PAYMENT_HISTORY_FOUND 					= 3081, // No Bill Payment History Found
	NO_MERCHANT_PAYEE_FOUND 						= 3082, // No Merchant Payee Found
	PAYEE_NOT_FOUND			 						= 3083, // Payee not found
	PAYEE_NICKNAME_EXISTS	 						= 3122, // The payee's nickname already exists
	FROM_ACCOUNT_NOT_VALID			 				= 3084, // The account number is not valid. Please contact the branch for support.
	PAYEE_ACCOUNT_NOTSET			 				= 3085, // Payee Account is not set
	MULTIPLE_FORMS_FOUND_FOR_SINGLE_UUID		    = 3086, // Multiple forms found for the same UUID
	APPLICATION_JSON_IS_EMPTY					    = 3087, // Provided application JSON Object is empty
	INVALID_JSON_ELEMENT_TYPE					    = 3088, // Invalid JSON element= s
	FORM_UUID_NOT_FOUND							    = 3089, // No Form found by provided uuId
	MOBIBRANCH_APPLICATION_NOT_UPDATED 				= 3090, // Application not updated
	REDIRECTION_IS_FAILED							= 3091, // Redirection is failed
	MOBIBRANCH_APPLICATION_TYPE_NOT_FOUND 			= 3092, // Application Type not Found

	MOBIBRANCH_MOBISIGN_IMAGE_URL_NOT_FOUND			= 3093, // MobiSign Image Url not Found
	MOBIBRANCH_MOBISIGN_REQUEST_NOT_FOUND			= 3094, // MobiSign Request not Found
	MOBIBRANCH_MOBISIGN_IMAGE_NOT_SUBMITTED			= 3096, // MobiSign Image Request Not Submitted
	MOBIBRANCH_MOBISIGN_MISSING_FIELDS				= 3097, // MobiSign Missing Fields
	MOBIBRANCH_APPLICATION_NOTE_NOT_FOUND 			= 3098, // Application Note not Found
	MOBIBRANCH_APPLICATION_NOTE_NOT_SAVED 			= 3099, // Application Note not Saved
	MOBIBRANCH_NO_CORRESPODING_FILES_FOUND 			= 3100, // No corresponding file= s
	MOBIBRANCH_NO_FOLDER_UUID_FOUND		 			= 3101, // No folder with provided uuId found
	MOBIBRANCH_EMAIL_TRANSMISSION_ERROR	 			= 3102, // Email transmission error
	MOBIBRANCH_NO_BRANCH_MATERIAL_FOUND 			= 3103, // No File Found
	MOBIBRANCH_TEMPLATE_FORMS_NOT_FOUND	 			= 3104, // Template form= s
	MOBIBRANCH_ERROR_FETCHING_TEMPLATE_FORM			= 3105, // Error fetching template form
	MOBIBRANCH_FORMS_NOT_FOUND	 					= 3106, // Form= s
	MOBIBRANCH_APPLICATION_NOT_FOUND	 			= 3107, // Application= s
	MOBIBRANCH_DB_APPLICATION_DATA_CORRUPTED 		= 3108, // Database Application data are corrupted
	MOBIBRANCH_APPLICATION_NO_CONTRACTS		 		= 3109, // No contracts found for this application
	MOBIBRANCH_ERROR_GENERATING_HTML_CONTRACT		= 3110, // Error generating HTML contract
	MOBIBRANCH_ERROR_GENERATING_PDF_CONTRACT		= 3111, // Error generating PDF contract
	MOBIBRANCH_ERROR_GETTING_SIDEBAR_CONTRACT		= 3112, // Error getting Sidebar contract
	MOBIBRANCH_APPLICATION_ERROR_UPDATING_STATUS	= 3113, // Error updating Application status
	// MOBIBRANCH_APPLICATION_ERROR_CHECKING_STATUS	= 3113, // Error checking Application status
	MOBIBRANCH_APPLICATION_BANKING_CORE_SUBMISSION_FAILURE	= 3114, // Banking Core submission failure
	MOBIBRANCH_CONTRACTS_CODE_FETCHING_FAILURE		= 3115, // Contracts code fetching failure
	MOBIBRANCH_CONTRACTS_CODE_SAVING_FAILURE		= 3116, // Contracts code saving failure
	MOBIBRANCH_APPLICATION_ROUTING_FAILURE			= 3117, // Application routing failure
	NO_MERCHANT_BILLING_ACCOUNT_FOUND 				= 3118, // Invalid Merchant Billing Account
	MERCHANT_NOT_FOUND 								= 3119, // Invalid Merchant
	MERCHANT_BALANCE_NOT_SUPPORTED 					= 3120, // Merchant Balance Not Supported
	MERCHANT_INQUIRY_FAILED 						= 3121, // Failed to retrieve balance
	//												  3122 is taken
	
	MOBIBRANCH_APPLICATION_OPERATION_NOT_ALLOWED_BY_STATUS	= 3201, // This operation is not allowed due to current application status
	MOBIBRANCH_APPLICATION_STATUS_SAVED				= 3310, // Application saved
	MOBIBRANCH_APPLICATION_STATUS_IQ_REQUIRED		= 3311, // Application requires IQ
	MOBIBRANCH_APPLICATION_STATUS_IQ_PASSED			= 3312, // Application IQ passed
	MOBIBRANCH_APPLICATION_STATUS_IQ_FAILED			= 3313, // Application IQ failed
	MOBIBRANCH_APPLICATION_STATUS_SUBMITTED			= 3320, // Application submitted
	MOBIBRANCH_APPLICATION_STATUS_PENDING			= 3324, // Application pending
	MOBIBRANCH_APPLICATION_STATUS_APPROVED			= 3325, // Application approved
	MOBIBRANCH_APPLICATION_STATUS_REJECTED			= 3326, // Application rejected
	MOBIBRANCH_APPLICATION_STATUS_SIGNED			= 3330, // Application signed
	MOBIBRANCH_APPLICATION_STATUS_COMPLETED			= 3340, // Application completed
	MOBIBRANCH_APPLICATION_CONTRACTS_MAPPING_NOT_FOUND	= 3341, // Application contracts mapping not found
	MOBIBRANCH_APPLICATION_NO_CONTRACTS_FOR_MAPPING	= 3342, // No contracts required for this mapping code
	MOBIBRANCH_APPLICATION_DB_CONTRACT_RECORD_INVALID	= 3343, // Application-contract record is invalid
	MOBIBRANCH_APPLICATION_FAILURE_GETTING_DYNAMIC_CONTRACT_JSON = 3344, // Failure getting DynamicContractJson
	MOBIBRANCH_APPLICATION_FAILURE_FINILIZING_PDF_CONTRACTS = 3345, // Failure finilizing PDF contracts
	SERVER_MISCONFIGURATION 						= 3346, // Server misconfiguration
	ERROR_MERGING_JSON_DATA							= 3347, // Error merging Json data
	IMAGE_LINK_ALREADY_EXISTS						= 3400, // Image already exists in position
	DUPLICATE_TARGET_BANNER_RULE					= 3401, // The rules cannot be the same
	
	APPLICATION_IN_PROCESS 							= 4001, // Application in process
	APPLICATION_SUBMITTED 							= 4002, // Application Submitted
	APPLICATION_ID_VERIFIED 						= 4003, // Application ID Verified
	APPLICATION_ID_DENIED 							= 4004, // Application ID Denied
	APPLICATION_CREDIT_SCORE_APPROVED 				= 4005, // Application Credit Score Approved
	APPLICATION_CREDIT_SCORE_DENIED 				= 4006, // Application Credit Score Denied
	APPLICATION_CREDIT_SCORE_APPROVED_USER_ACCEPT 	= 4007, // Application Credit Score Approved and User Accepted
	APPLICATION_CREDIT_SCORE_APPROVED_USER_REJECT 	= 4008, // Application Credit Score Approved and User Rejected
	APPLICATION_EFT_COMPLETED 						= 4009, // Application EFT Completed
	
	APPLICATION_TERMS_CONDITIONS_ACCEPTED 			= 4010, // Application Terms and Condition Accepted
	APPLICATION_FI_REVIEW 							= 4011, // Application Under Review
	APPLICATION_FI_APPROVED 						= 4012, // Application Approved After Review
	APPLICATION_FI_DENIED 							= 4013, // Application Denied After Review
	APPLICATION_LOAN_CREATE_COMPLETED 				= 4014, // Application Approved and Loan Create Completed
	APPLICATION_FUND_LOAN_COMPLETED 				= 4015, // Application Fund Loan Completed
	
	INVALID_RANGE			 						= 4016, // Invalid Amount Range
	INVALID_AMOUNT_FORMAT			 				= 4017, // Invalid Amount Format
	INVALID_MIN_AMOUNT			 					= 4018, // Invalid Min Amount 
	INVALID_MAX_AMOUNT				 				= 4019, // Invalid Max Amount 
	
	TRANSACTION_FAILED					 			= 4020, // Transaction Failed
	INVALID_FILE_FORMAT								= 4021, // The file format is not valid. Please contact the branch for support.
	ERROR_SENDING_EMAIL								= 4022, // Error Sending Email
	ERROR_GENERATING_DOWNLOAD_RESOURCE				= 4423, // An error occurred generating the statement. Please contact the branch for support.
	EMAIL_SENT_SUCCESSFULLY							= 4023, // Email Sent Successfully
	TRANSFER_BY_TEXT_ACCOUNT_CREATION_SUCCESS		= 4024, // Transfer By Text Account Created Successfully
	Duplicate_Nickname								= 4025, // Duplicate Nickname not allowed
	NON_MATCHING_CURRENY_CODE	 					= 4026, // Currency Code doesn't match for FROM and TO Accounts
	MUST_AGREE_TO_TERMS								= 4027, // Must agree to terms
	NO_TRANSFER_BY_TEXT_ACCOUNTS					= 4028, // NO Transfer  By Text Accounts Found
	TRANSFER_BY_TEXT_ACCOUNT_DELETION_SUCCESS		= 4029, // Transfer By Text Account Deleted Successfully
	
	NO_BUDDIES										= 4030, // No Peers Found!
	BUDDY_PAY_ACCOUNT_CREATION_SUCCESS				= 4031, // Peer Payment Account Created Successfully
	ACCOUNT_PAYBYTEXTACCOUNT_EQUAL 					= 4032, // Account and Pay by text account cannot be same
	BUDDY_NOT_FOUND   								= 4033, // Peer Not Found
	INVALID_BUDDY_ACCOUNT_TYPES	      				= 4034, // Invalid Peer Account Types
	SAME_USER_CANNOT_BUDDY							= 4035, // Same User cannot be Peer
	BUDDY_NOT_CREATED								= 4036, // Peer not created
	BUDDY_PAYMENT_DAILY_MAX_COUNT_EXCEEDED		    = 4090, // You have already submitted the maximum number of Peer Payments for today.
	NO_BANNERS_FOUND								= 4037, // No Banners Found
	PLACEMENT_NOT_FOUND								= 4038, // Placement Parameter not found
	INVALID_SCREEN									= 4039, // Invalid Screen
	
	INVALID_PAGENUMBER								= 4040, // Invalid Page Number
	INVALID_REQUEST									= 4041, // Invalid Request
	SMS_RECEIVED_SUCCESSFULLY						= 4042, // SMS sent Succesfully
	CANNOT_DISABLE									= 4043, // Trying to Disable an Alert that is not Enabled
	NewLanguage_sameAs_Old							= 4044, // New Language same as old
	NewNetwork_sameAs_Old							= 4045, // New Network same as old
	NewEmails_sameAs_Old							= 4046, // New Emails same as old
	NewMobilearthId_sameAs_Old      				= 4047, // New MobilearthId is same as Old
	NewTimezone_sameAs_Old							= 4048, // New Timezone is same as Old
	NewSubscribe_sameAs_Old							= 4049, // New Subscribe is same as Old
	
	NewTextme_sameAs_Old							= 4050, // New Text message flag is same as old
	INVALID_MOBILEARTHID            				= 4051, // Invalid Mobile Id
	INVALD_IDField									= 4052, // Id to be updated cannot be null
	CARDTYPE_INVALID								= 4053, // Invalid card type
	REASON_INVALID									= 4054, // Invalid reason
	REPORT_LOST_STOLEN_CARD_ORDER_INVALID 			= 4056, // Invalid report lost/stolen card order
	REPORT_LOST_STOLEN_CARD_ORDER_NOT_CREATED 		= 4057, // Report lost/stolen card order not created
	
	RDC_HISTORY_REQUEST_ERROR	    				= 4060, // RDC HISTORY REQUEST ERROR
	RDC_IMAGE_ITEM_REQUEST_ERROR					= 4061, // RDC IMAGE ITEM REQUEST ERROR
	RDC_DEPOSIT_REQUEST_ERROR	    				= 4062, // RDC DEPOSIT REQUEST ERROR
	SELF_REGISTRATION_USER_CREATION_ERROR     		= 4065, // Self Registration User Creation Error
	SELF_REGISTRATION_MFA_REPEAT_SUBMISSION_ERROR	= 4066, // Self Registration Repeat Submission Error
	INVALID_TOKEN                    				= 4067, // Invalid Token
	SELF_REGISTRATION_MFA_ERROR     				= 4068, // Self Registration MFA Error
	EQUIFAX_HARDPULL_NOT_SUCCESSFULL 				= 4069, // Hard Pull Not Successful
	
	USER_FORGOT_PASSWORD_LIMIT_ERROR				= 4070, // Password daily reset limit exceeded

	SELF_REGISTRATION_MFA_NOT_INITIALIZED			= 4071, // Self Registration was not properly initialized
	SELF_REGISTRATION_MFA_CHALLENGE_FAILED			= 4072, // Self Registration challenge not answered correctly
	SELF_REGISTRATION_MFA_CHALLENGE_COUNT_FAILED	= 4073, // Self Registration challenge not answered correctly too many times
	SELF_REGISTRATION_MFA_CODE_EXPIRED			    = 4074, // Self Registration MFA code has expired
												
												//   4090, // !!! TAKEN - The maximum number of Peer Payments is already submitted for today.
	
	INVALID_REQUEST_DATA							= 4100, // Invalid Request Data
	INVALID_CIF_NUMBER								= 4101, // Invalid CIF Number
	WEBSERVICE_ERROR								= 4102, // Failure Response From Loan Web Service
	INVALID_LOAN_NUMBER								= 4103, // Invalid Loan Number
	INVALID_CIF_NUMBER_SELF_REG						= 4104, // Invalid CIF Number. Still Using Self Registration Temp CIF number.
	FUND_LOAN_ERROR									= 4105, // Funding Loan Is Not Successful
	NO_APPROVED_APPLICATION							= 4106, // No Approved Application Found
	NO_COMPLETED_APPLICATION						= 4107, // No Completed Application Found. Contract Is Generated.
	NO_APPLICATION_DATA								= 4108, // No Application Detail Found
	INVALID_CREDIT_SCORE							= 4109, // Invalid Credit Score
	GENERATE_CONTRACT_ERROR							= 4110, // Internal Error. Cannot Generate Contract At the Moment
	
	FORM_STATUS_NOT_FUNDED							= 4111, // Application Form Status Is Not Updated. Loan Is Funded. Contract Is Generated.
	CUSTOMER_NO_NOT_UPDATED							= 4112, // Loan Is Created. Error in Updating Customer Number.
	FORM_ACCOUNTID_NOT_UPDATED						= 4113, // Application Account Id Is Not Updated Successfully. Application Form Status Updated Successfully. Loan Created Successfully.
	FORM_STATUS_NOT_UPDATED							= 4114, // Application Form Status Is Not Updated. Loan created successfully.
	MARKETING_SUBSCRIBE_NOT_UPDATED					= 4115, // Error in Updating Marketing Subscribe.
	
	APPLICATION_FIELD_NOT_FOUND 							= 4120, // Requested application field= s
	APPLICATION_CREDITSCORE_REJECT_STATUS_UPDATE_FAILURE 	= 4121, // Credit score denied, // but failed to update application status
	APPLICATION_ID_DENIED_STATUS_UPDATE_FAILURE				= 4122, // ID denied, // but failed to update application status
	EMAIL_PAYOUT_STATEMENT_FAILED_INVALID_PARAM				= 4123, // Email payout statement is failed. Invalid parameter.
	EMAIL_PAYOUT_STATEMENT_FAILED_INVALID_TEMPLATE_PATH		= 4124, // Email payout statement is failed. Invalid template path.
	
	MOBISIGN_REQUEST_LIST_IS_EMPTY					= 4130, // MobiSign request list is empty
	INVALID_MOBISIGN_REQUEST_ID						= 4131, // MobiSign request ID is invalid
	MOBISIGN_REQUEST_CREATION_FAILURE				= 4132, // MobiSign request creation failure
	MOBISIGN_ERROR_SIGNING_CONTRACT					= 4133, // Error signing contract
	MOBISIGN_IMAGE_SUBMISSION_FAILURE				= 4134, // MobiSign image submission failure
	MOBISIGN_DELETION_FAILURE						= 4135, // MobiSign deletion failure
	
	LOAN_WEBSERVICE_IS_DOWN							= 4140, // Loan Service Is Not Available
	CREDIT_SCORE_WEBSERVICE_IS_DOWN					= 4141, // Credit Score Service Is Not Available
	IQ_WEBSERVICE_IS_DOWN							= 4142, // Credit Identification Service Is Not Available
	IQ_WEBSERVICE_CONNECTION_FAILURE				= 4143, // Credit Identification Service connection failure
	EQUIFAX_BEAN_CONSTRUCTION_FAILURE				= 4144, // Equifax bean construction failure
	IQ_REQUEST_REJECTED								= 4145, // Credit Identification Request Rejected
	IQ_ANSWERS_REJECTED								= 4146, // Credit Identification Answers Rejected

	
	INVALID_MESSAGE									= 4150, // Message Does Not Have a Valid Format.
	INVALID_FILE_NAME								= 4151, // File Name Does Not Have a Valid Format.
	INVALID_FILE_TYPE								= 4152, // File Type Does Not Have a Valid Format.
	INVALID_FILE_SIZE								= 4153, // File Size Is Not Valid.
	INVALID_REFERENCE_NO							= 4154, // Invalid Reference No.
	INVALID_REOPEN_STATUS_MESSAGE					= 4155, // Only Completed Message Can Be Reopened.
	EMAIL_ADDRESS_NOT_FOUND							= 4156, // Email address is not found
	
	// INVALID_DISCLOSURE								= 4156, // Invalid Disclosure
	
	SIT_WEBSERVICE_IS_DOWN							= 4160, // SIT Service Is Not Available
	
	INVALID_VOUCHER_IMAGE							= 4170, // Voucher Image Is Not Available
	CHECK_IMAGE_SUBSYSTEM_UNAVALABLE				= 4171, // Check Image Subsystem Is Not Available

	CHAT_GROUP_OWNER_IS_LAST						= 4180, // Cannot remove/demote the last owner of a chat group.
	CHAT_GROUP_CALLER_IS_NOT_OWNER					= 4181, // Only an owner of a chat group can modify it.
	CHAT_GROUP_CALLER_IS_NOT_MEMBER					= 4182, // Only a member or owner of a chat group can get the information about it.
	CHAT_GROUP_TARGET_IS_NOT_MEMBER					= 4183, // The target user is already not a member of the group.
	CHAT_GROUP_DOES_NOT_EXIST					    = 4184, // The target chat group does not exist.
	CHAT_GROUP_WITH_SAME_NAME_EXISTS				= 4185, // A chat group with the same name already exists.
	USER_ALREADY_INVITED							= 4186, // The user is already invited.
	LASERFICHE_NOT_CONFIGURED						= 4187, // Laserfiche connection is not configured.

	HOLIDAY_SAVINGS_NOT_CREATED						= 4190, // Holiday Savings share was not created.
	HOLIDAY_SAVINGS_AUTOMATIC_TRANSFER_NOT_CREATED	= 4191, // Holiday Savings share created, // but setup of automatic transfers failed.
	HOLIDAY_SAVINGS_MATURITY_TRANSFER_NOT_CREATED	= 4192, // Holiday Savings share created, // but setup of maturity transfer failed.
	HOLIDAY_SAVINGS_AUTOMATIC_AND_MATURITY_NOT_CREATED	= 4193, // Holiday Savings share created, // but setup of automatic transfers AND maturity transfer failed.
	
	SSB_WEBSERVICE_IS_DOWN							= 4200, // Social Security Board Service Is Not Available
	
	IQ_ERROR_FIELD_CHECKS_FAILED					= 4300, // Field Checks Failed In Credit Identification Service
	IQ_ERROR_APPLICATION_VERIFICATION				= 4301, // Application Verification Error In Credit Identification Service
	IQ_ERROR_FRAUD_CHECK_FAILED						= 4302, // Fraud Check Failed In Credit Identification Service
	IQ_ERROR_ASSESMENT_COMPLETE						= 4303, // Assesment Complete Error In Credit Identification Service
	IQ_ERROR_SYSTEM_PROBLEM							= 4304, // System Problem In Credit Identification Service
	IQ_ERROR_UNDEFINED								= 4305, // Undefined Error In Credit Identification Service
	
	INVALID_USER_ID									= 4310, // Invalid User Id
	USER_INVALID_PASSWORD_CONFIRM_PASS				= 4311, // The input password must be equal to the confirm password
	USER_OWNS_PROSPECTS_CANNOT_DELETE				= 4312, // Cannot delete an user who owns prospects. Please route the prospects to another user first.
	USERS_EXIST_IN_ROLE								= 4313, // Cannot Delete Role as it exists on a User Account. Please remove from user account first.
	ROLE_ALREADY_EXISTS								= 4314, // The role already exists.
	ROLE_DOES_NOT_EXIST								= 4315, // The role does not exist.
	APP_AUTHENTICATOR_REJECTED						= 4316, // App Authenticator rejected permission.
	APP_AUTHENTICATOR_NEEDS_ACTIVATION				= 4317, // App Authenticator must be activated.
	
	HD_INVALID_CATEGORY								= 4400, // Invalid Help Desk Category
	HD_TICKET_ALREADY_OWNED							= 4401, // Ticket already owned
	HD_TICKET_NOT_FOUND								= 4402, // Ticket not found
	HD_TICKET_ALREADY_CLOSED						= 4403, // Ticket already closed
	HD_CATEGORY_NOT_FOUND							= 4404, // Help Desk Category not found
	HD_CATEGORY_TARGET_IS_NOT_OBSERVER				= 4405, // The target user is not observer of the category
	HD_TICKET_ALREADY_OPEN							= 4406, // Ticket already open
	HD_OUTSIDE_BUSINESS_HOURS						= 4407, // Outside of business hours
	
	TS_INVALID_CATEGORY								= 4410, // Invalid Time Sheet Category
	TS_REPORT_NOT_FOUND								= 4411, // Time Sheet Report not found
	TS_CATEGORY_NOT_FOUND							= 4412, // Time Sheet Category not found
	TS_CATEGORY_EXISTS								= 4413, // The Time Sheet Category already exists
	TS_PARENT_CATEGORY_NOT_FOUND					= 4414, // The Parent Category could not be found
	TS_PARENT_SUBCATEGORY							= 4415, // Parent Category is a subcategory

	EXPENSE_INVALID_DATE							= 4420, // Invalid Expense Date

	PAYROLL_DEPARTMENT_HAS_BATCHES					= 4425, // The payroll department has batches and cannot be deleted
	PAYROLL_ACCESS_GROUP_IN_USE						= 4426, // The payroll access group has associated Payroll Groups and cannot be deleted
	PAYROLL_BATCH_NOT_FOUND							= 4427, // The payroll batch was not found.
	PAYROLL_DEPARTMENT_NOT_FOUND					= 4428, // The payroll department was not found.
	PAYROLL_FILE_NOT_FOUND							= 4429, // The payroll upload file was not found.
	PAYROLL_BATCH_ALREADY_UNDER_PROCESSING			= 4430, // The payroll batch is being processed elsewhere.
	PAYROLL_BATCH_APPROVED_BUT_NOT_POSTED			= 4431, // The payroll batch was approved but requires more signatures.
	PAYROLL_BATCH_APPROVED_BUT_FAILED_TO_POST		= 4432, // The payroll batch was approved but failed to post.
	PAYROLL_PENDING_TRANSACTION_NOT_FOUND			= 4433, // The corresponding pending transaction for the payroll was not found.
	PAYROLL_FIRST_NAME_INVALID						= 4434, // The first name of the payee is not valid.
	PAYROLL_LAST_NAME_INVALID						= 4435, // The last name of the payee is not valid.
	PAYROLL_MOBILE_ID_INVALID						= 4436, // The Mobile Id of the payee is not valid.
	PAYROLL_PAYEE_ACCOUNT_INVALID					= 4437, // The account of the payee is not valid.
	PAYROLL_PAYEE_ACCOUNT_TYPE_INVALID				= 4438, // The account type of the payee is not valid.
	PAYROLL_PAYEE_INSTITUTION_INVALID				= 4439, // The institution of the payee is not valid.
	PAYROLL_PAYMENT_AMOUNT_INVALID					= 4440, // The payment amount is not valid.
	PAYROLL_PAYMENT_DESCRIPTION_INVALID				= 4441, // The payment description is not valid.
	PAYROLL_BATCH_NOT_EDITABLE						= 4442, // The payroll batch is not editable.
	PAYROLL_BATCH_DUPLICATE_ENTRY					= 4443, // The payroll with the same title already exists.
	PAYROLL_BATCH_CONTAINS_INVALID_DATA				= 4444, // The payroll batch contains invalid data.
	PAYROLL_BATCH_ACCOUNT_NOT_FOUND					= 4445, // The account the batch is being posted from is not found.
	PAYROLL_BATCH_TITLE_INVALID						= 4446, // The payroll title is invalid.
	PAYROLL_BATCH_DESCRIPTION_INVALID				= 4447, // The payroll description is invalid.
	PAYROLL_PENDING_APPROVED_BY_POSTER				= 4448, // A pending payroll cannot be approved by its own poster.
	PAYROLL_DEPARTMENT_NAME_INVALID					= 4449, // The payroll department name is invalid.
	PAYROLL_FILE_UPLOAD_ERROR						= 4450, // Upload failed! Could not find a file to upload.
	PAYROLL_FILE_UPLOAD_OVERENTRIES					= 4451, // Upload failed! Number of payroll entries exceeded.
	PAYROLL_FILE_UPLOAD_INVALID_FILE_TYPE			= 4452, // Upload failed! Invalid file type detected.
	PAYROLL_FILE_UPLOAD_OVERSIZE					= 4453, // Upload failed! Size limit was exceeded.
	PAYROLL_BATCH_TOTAL_AMOUNT_EXCEEDED				= 4454, // The payroll batch total amount is exceeded.
	PAYROLL_BATCH_CC_NOT_ALLOWED					= 4455, // The payroll batch contains external payments that cannot be paid using a credit card.

	PAYMENT_TYPE_NOT_ALLOWED						= 4460, // This payment type is not allowed for this transaction.
	PAYMENT_DAILY_LIMITS_EXCEEDED					= 4461, // The transaction exceeds the account daily limit on this payment type. Please contact the branch for more details.

	SCHEDULE_PAYMENT_FAILED							= 4480, // Schedule payment failed.
	SCHEDULE_PAYMENT_DATE_IN_THE_PAST				= 4481, // Only can Schedule payments for future dates.
	SCHEDULE_PAYMENT_NOT_FOUND						= 4482, // Scheduled payment not found.
	SCHEDULE_PAYMENT_SIGNATURES_NOT_SUPPORTED		= 4483, // Multi-signature account cannot be used in scheduled transactions.
	SCHEDULE_PAYMENT_EXISTS							= 4484, // Scheduled Payment exists.
	
	SCHEDULE_ORDER_FAILED							= 4485, // Schedule order failed.
	SCHEDULE_ORDER_DATE_IN_THE_PAST					= 4486, // Only can Schedule orders for future dates.
	SCHEDULE_ORDER_NOT_FOUND						= 4487, // Scheduled order not found.
	SCHEDULE_ORDER_SIGNATURES_NOT_SUPPORTED			= 4488, // Multi-signature account cannot be used in scheduled orders.
	SCHEDULE_ORDER_EXISTS							= 4489, // Scheduled Order exists.

	INTERNAL_NOTE_NOT_FOUND							= 4490, // Internal note not found.
	PDF_STATEMENTS_NOT_FOUND						= 4491, // PDF Statements are not available to this account.
	
	TWILIO_PERMIT_INCOMING_NOT_ENABLE				= 4492, // Incoming Twilio message detected. Rejected since twilioPermitIncoming is not enabled.

	QUEUE_REQUEST_ALREADY_ROUTED					= 4500, // Queue request already routed
	QUEUE_REQUEST_NOT_FOUND							= 4501, // Queue request not found
	QUEUE_REQUEST_ALREADY_CLOSED					= 4502, // Queue request already closed
	QUEUE_REQUEST_ALREADY_OPEN						= 4503, // Queue request already open
	QUEUE_OUTSIDE_BUSINESS_HOURS					= 4504, // Outside of business hours
	QUEUE_REQUEST_INVALID_LOCATION					= 4505, // Invalid queue location
	QUEUE_REQUEST_NOT_ROUTED						= 4506, // Queue request not routed
	QUEUE_REQUEST_MULTIPLE_ROUTING_FORBIDDEN		= 4507, // Multiple Routing Forbidden
	
	TOPUP_BAD_INSTITUTION_CONFIG					= 4550, // Institution not configured correctly
	TOPUP_INSTITUTION_BALANCE_INSUFFICIENT			= 4551, // Institution balance is insufficient
	TOPUP_MODULE_NOT_ENABLED						= 4552, // The Topup Server module is not enabled.
	TOPUP_CONNECTION_ERROR							= 4553, // The Topup Server connection failed.
	TOPUP_UNKNOWN_TELCO								= 4554, // Phone Number does not have a Telco
	TOPUP_TRIGGERED_TELCO							= 4555, // Some mobile operators are not available right now. If you do not see your payees or operators to top up, please try again later.
	LITE_APPLICATIONS_MODEL_NOT_FOUND				= 4600, // Application Model not found
	LITE_APPLICATIONS_TEMPLATE_NOT_FOUND			= 4601, // Application Template not found
	LITE_APPLICATIONS_TEMPLATE_DISABLED				= 4602, // Application Template disabled
	LITE_APPLICATIONS_NOT_VALID						= 4603, // Application not valid
	LITE_APPLICATIONS_FAILED_ATTACHMENT				= 4604, // Failed to save application attachments.
	
	CORPORATE_PAYMENT_INVALID_CUSTOMER				= 4620, // Invalid corporate customer Id.
	CORPORATE_PAYMENT_USER							= 4621, // User is a Corporate Payment User.
	CORPORATE_PAYMENT_EXISTING_PHONE				= 4622, // A customer exists with that phone number
	CORPORATE_PAYMENT_EXISTING_ID					= 4623, // A customer exists with that customer ID

	DUPLICATE_TELLER_DRAWER							= 4624, // A Teller Drawer with that Id already exists.
	MULTIPLE_CENTRAL_TELLER_DRAWER					= 4625, // A Central Teller Drawer already exists.
	BALANCE_REQUEST_HANDLED							= 4626, // The balance request has already been handled.

	SIGNATURE_CONFIGURATION_NOT_AVAILABLE			= 4630, // The signature feature is not configured for this document.

	CIF_UPDATE										= 4635, // Customer Information update required.
	FORCE_CIF_UPDATE								= 4636, // Customer Information update required.
	
	GROUP_NOT_FOUND									= 4640, // Unknown Group
	GROUP_ALREADY_EXISTS							= 4641, // Group already exists with that name
	
	TOO_MANY_REQUESTS								= 4642, // Attempt to exceed the daily number of requests

	UC_COMPANY_ID_TAKEN								= 4645, // Company ID already taken
	UC_COMPANY_ID_INVALID							= 4646, // Invalid Company ID
	INVALID_ROLE									= 4647, // Invalid Role
	
	// Batch Order File 
	BATCH_FILE_UPLOAD_ERROR							= 4701, // Upload failed! Please select a file to upload.
	BATCH_FILE_UPLOAD_INVALID_FILE_TYPE				= 4702, // Upload failed! Invalid file type detected.
	BATCH_FILE_UPLOAD_FILE_EMPTY_LINE				= 4703, // Upload failed! The batch file is empty.
	BATCH_FILE_UPLOAD_OVER_ENTRIES					= 4704, // Upload failed. A batch can have maximum {n} number of orders.
	BATCH_FILE_UPLOAD_OVERSIZE						= 4705, // Upload failed! Uploaded failed. Size limit of {n} bytes was exceeded.
	BATCH_FILE_UPLOAD_COPY_ERROR					= 4706, // Upload failed! Could not copy file to file repository.
	BATCH_FILE_MISSING_ATTACHMENT		            = 4707, // Submit failed! Attachment file is missing.
	BATCH_FILE_OVER_MAX_ATTACHMENT		            = 4708, // Cannot attach more than {n} documents to the batch.
	BATCH_FORM_MISSING_BATCH_ID						= 4709, // Batch ID is missing.
	BATCH_FORM_MISSING_ACTION						= 4710, // Batch Action is missing.
	BATCH_FORM_MISSING_ORDER_TYPE					= 4711, // Batch Order Type is missing.
	BATCH_FORM_MISSING_PAYMENT_ACCOUNT				= 4712, // Batch Payment Account is missing.
	BATCH_FORM_INVALID_ACTION						= 4713, // Batch Action is invalid.

	BATCH_ORDER_NOT_FOUND							= 4730, // The batch order was not found.
	BATCH_ORDER_NOT_ALLOWED_DELETE					= 4731, // The batch order status is not allowed to delete.
	BATCH_ORDER_NOT_ALLOWED_SUBMIT					= 4732, // The batch order status is not allowed to submit.
	BATCH_ORDER_NOT_ALLOWED_APPROVE					= 4733, // The batch order status is not allowed to approve.
	BATCH_ORDER_NOT_ALLOWED_REJECT					= 4734, // The batch order status is not allowed to reject.
	BATCH_ORDER_NOT_ALLOWED_CANCEL					= 4735, // The batch order status is not allowed to cancel.
	BATCH_ORDER_USER_NOT_ALLOWED_DELETE				= 4740, // The batch order status is not allowed to delete by this user.
	BATCH_ORDER_USER_NOT_ALLOWED_SUBMIT				= 4741, // The batch order status is not allowed to submit by this user.
	BATCH_ORDER_USER_NOT_ALLOWED_APPROVE			= 4742, // The batch order status is not allowed to approve by this user.
	BATCH_ORDER_USER_NOT_ALLOWED_REJECT				= 4743, // The batch order status is not allowed to reject by this user.
	BATCH_ORDER_USER_NOT_ALLOWED_CANCEL				= 4744, // The batch order status is not allowed to cancel by this user.
	BATCH_ORDER_CONCURRENT_ACCESS					= 4745, // The batch order status is already updated by another user.
	BATCH_ORDER_USER_NOT_ENOUGH_LIMIT				= 4746, // This user does not have enough limit to approve.

	EKYC_SCAN_ERROR									= 4750, // Scanning Error
	ACCOUNT_MONITORING_REVIEW						= 4780, // Your transaction is currently under review. You will be alerted on the next steps.
	ACCOUNT_MONITORING_SUSPENDED					= 4781, // Your transaction cannot be completed at this time.

	EXTERNAL_QR_NO_PERMISSION						= 4760,
	EXTERNAL_QR_EXTERNAL_ONLY						= 4761,
	EXTERNAL_QR_EXPIRED								= 4762,
	QR_CODE_NOT_FOUND								= 8997,

	MODULE_NOT_ENABLED								= 8999, // The module is not enabled.
	INVALID_DATA 									= 9000, // Invalid Data
	
	ALL_CODE_HANDLER 								= -999999999 // All code handlers			

}
